<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAccountInfoQuery } from '@/platform/identity/queries';
import { Path } from '@/platform/routing/targets';

// Account billing reminder dialog. Trigger codes:
// 1 = CARD_UNBOUND, 2 = CARD_INVALID, 3 = LATEST_PAY_FAILED. Title + body
// + button affordance vary by code; root-account "Go to billing" lands on
// /billing, non-root closes.
const TRIGGER_CODES = [1, 2, 3] as const;

const { data: accountInfo } = useAccountInfoQuery();
const router = useRouter();
const { t } = useI18n();

const showTrialEnd = ref(false);

const remindCode = computed(() => accountInfo.value?.remindCode);
const noCreditCard = computed(() => remindCode.value === 1);
const creditCardInvalid = computed(() => remindCode.value === 2);
const debitFailure = computed(() => remindCode.value === 3);
const isRed = computed(() => debitFailure.value || creditCardInvalid.value);
const isRootAccount = computed(() => !!accountInfo.value?.isRootAccount);

const title = computed(() => {
  const code = remindCode.value;
  if (code === 1) return t('trial.end.title');
  if (code === 2) return t('trial.invalid.title');
  if (code === 3) return t('trial.debit.title');
  return '';
});

// Re-open on every remindCode transition into {1,2,3}. User can dismiss via
// the button; the next remindCode change re-triggers the dialog.
watch(
  remindCode,
  (code) => {
    showTrialEnd.value = !!code && TRIGGER_CODES.includes(code as 1 | 2 | 3);
  },
  { immediate: true },
);

function handleConfirm(): void {
  if (isRootAccount.value) {
    void router.push(Path.Billing);
  }
  showTrialEnd.value = false;
}
</script>

<template>
  <ElDialog
    v-model="showTrialEnd"
    :title="title"
    width="550px"
    :class="['trial-end-dialog', { 'is-red': isRed }]"
    :close-on-click-modal="false"
  >
    <div data-testid="trial-dialog">
      <p v-if="noCreditCard">
        <i18n-t keypath="trial.end.desc" tag="span">
          <a class="link" href="mailto:cloud-support@celerdata.com">
            cloud-support@celerdata.com
          </a>
        </i18n-t>
      </p>
      <p v-if="debitFailure">{{ t('trial.debit.desc') }}</p>
      <div v-if="creditCardInvalid">
        <p>{{ t('trial.invalid.desc1') }}</p>
        <p style="margin-top: 16px">{{ t('trial.invalid.desc2') }}</p>
      </div>
    </div>
    <template #footer>
      <ElButton
        :type="isRed ? 'danger' : 'primary'"
        data-testid="trial-dialog-confirm"
        @click="handleConfirm"
      >
        {{ isRootAccount ? t('trial.confirm') : t('common.ok') }}
      </ElButton>
    </template>
  </ElDialog>
</template>

<style scoped lang="scss">
.trial-end-dialog {
  .link {
    text-decoration: underline;
    color: var(--el-color-primary);
    cursor: pointer;
  }
}
</style>
